import type { StateCreator } from 'zustand/vanilla';
import type { BackfillTaskType } from '@/app/types';

type FlyConfig = {
  duration?: number;
  delay?: number;
  ease?: string | number[];
  opacity?: {
    from?: number;
    to?: number;
  };
  scale?: {
    from?: number;
    to?: number;
  };
};

export type FlyType = BackfillTaskType;

export type FlyState = {
  flyCreatedAt: number | null;
  flyTtlMs: number;
  flyConfig: FlyConfig | null;
  flyEndId: string | null;
  flySeq: number;
};

export interface FlyStore extends FlyState {
  setFlyPending: ({
    cfg,
    flyEndId,
    sourceEl,
    flyTtlMs,
    sourceSrc,
  }: {
    cfg?: FlyConfig;
    flyEndId: string;
    sourceEl: HTMLElement;
    flyTtlMs?: number;
    sourceSrc?: string;
  }) => void;
  clearFly: () => void;
  overlay: OverlayState | null;
  overlayPending: OverlayState | null;
  overlaySourceEl: HTMLElement | null;
  overlayFlyToElement: (el: HTMLElement | null, cfg?: FlyConfig) => void;
  overlayClear: () => void;
}

const initialFlyState: FlyState = {
  flyCreatedAt: null,
  flyTtlMs: 8000,
  flyConfig: null,
  flyEndId: null,
  flySeq: 0,
};

export const createFlySlice: StateCreator<FlyStore, [], [], FlyStore> = (set, get) => ({
  ...initialFlyState,
  overlay: null,
  overlayPending: null,
  overlaySourceEl: null,
  setFlyPending: ({ cfg, flyEndId, sourceEl, flyTtlMs, sourceSrc }) => {
    const rect = sourceEl.getBoundingClientRect();
    let next: OverlayState;
    if (sourceSrc) {
      next = {
        src: sourceSrc,
        start: rect,
        cfg,
      };
    } else if (sourceEl instanceof HTMLImageElement && sourceEl.currentSrc) {
      next = {
        src: sourceEl.currentSrc,
        start: rect,
        cfg,
      };
    }
    set(() => ({
      overlayPending: next,
      overlay: null,
      overlaySourceEl: sourceEl,
      flyCreatedAt: Date.now(),
      flyTtlMs: flyTtlMs ?? 8000,
      flyConfig: cfg,
      flyEndId: flyEndId,
      flySeq: (get().flySeq ?? 0) + 1,
    }));
  },

  clearFly: () => set(initialFlyState),

  overlayFlyToElement: (el, cfg) =>
    set((state) => {
      if (!el) return {} as Partial<FlyStore>;
      const rect = el.getBoundingClientRect();
      // 使用最新的起点信息，避免在 overlayBeginFromElement 时机过早导致的布局偏差
      const srcEl = state.overlaySourceEl;
      const baseRaw = state.overlay ?? state.overlayPending;
      if (!baseRaw) return {} as Partial<FlyStore>;
      const srcRect = srcEl?.getBoundingClientRect();
      const base: OverlayState =
        srcEl && srcRect
          ? {
              ...baseRaw,
              start: srcRect,
            }
          : baseRaw;
      return {
        overlayPending: null,
        overlay: {
          ...base,
          to: rect,
          cfg: {
            ...(base.cfg || {}),
            ...(cfg || {}),
          },
        },
      } as Partial<FlyStore>;
    }),
  overlayClear: () => set({ overlay: null, overlayPending: null, overlaySourceEl: null }),
});

export type OverlayState = {
  src: string;
  start: {
    left: number;
    top: number;
    width: number;
    height: number;
  };
  to?: {
    left: number;
    top: number;
    width: number;
    height: number;
  };
  cfg?: FlyConfig;
};
